import { createTheme } from "@mui/material/styles";
/**
 * Because these imports are not ts files, we cannot rewrite the import path while build
 * this leads to having absolute import paths in our published build
 * which then leads to typescript errors because the import paths cannot be resolved
 */

// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import MarkProMedium from "../../assets/fonts/MarkOT-Medium.otf";
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import EnBWDINPro from "../../assets/fonts/EnBWDINPro-Regular.ttf";
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import EnBWDINProMedium from "../../assets/fonts/EnBWDINPro-Medium.ttf";

import { createPaletteTheme } from "src/styles/senecBaseThemePalette";
import {
    senecAlertTheme,
    senecAutocompleteTheme,
    senecBadgeTheme,
    senecButtonTheme,
    SenecCheckboxTheme,
    senecChipTheme,
    senecDataGridTheme,
    senecFormControlTheme,
    senecFormHelperTextTheme,
    senecIconButtonTheme,
    senecInputBaseTheme,
    senecInputLabelTheme,
    senecMenuItemTheme,
    senecMenuTheme,
    senecNativeSelectTheme,
    senecOutlinedInputTheme,
    senecPaperTheme,
    senecSelectTheme,
    senecStepConnectorTheme,
    senecStepLabelTheme,
    senecStepperTheme,
    SenecTablePaginationTheme,
    senecTabsTheme,
    senecTabTheme,
    senecTooltipTheme,
} from "src/styles/components";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";
import { createTypographyTheme } from "src/styles/typography";
import { breakpoints } from "src/styles/breakpoints";

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        s1: true;
        s1Medium: true;
        s1Bold: true;
        s2: true;
        s2Medium: true;
        body1Responsive: true;
        body1Medium: true;
        buttonSmall: true;
        enbwTabletS1: true;
        enbwTabletS1Medium: true;
        enbwTabletS2Small: true;
        enbwTabletP1: true;
    }
}

declare module "@mui/material/styles" {
    interface TypographyVariants {
        s1: React.CSSProperties;
        s1Medium: React.CSSProperties;
        s1Bold: React.CSSProperties;
        s2: React.CSSProperties;
        s2Medium: React.CSSProperties;
        body1Responsive: React.CSSProperties;
        body1Medium: React.CSSProperties;
        buttonSmall: React.CSSProperties;
        enbwTabletP1: React.CSSProperties;
        enbwTabletS1: React.CSSProperties;
        enbwTabletS1Medium: React.CSSProperties;
        enbwTabletS2Small: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        s1?: React.CSSProperties;
        s1Medium?: React.CSSProperties;
        s1Bold?: React.CSSProperties;
        s2?: React.CSSProperties;
        s2Medium?: React.CSSProperties;
        body1Responsive?: React.CSSProperties;
        body1Medium?: React.CSSProperties;
        buttonSmall?: React.CSSProperties;
        enbwTabletP1?: React.CSSProperties;
        enbwTabletS1?: React.CSSProperties;
        enbwTabletS1Medium?: React.CSSProperties;
        enbwTabletS2Small?: React.CSSProperties;
    }
}

declare module "@mui/system" {
    interface BreakpointOverrides {
        xsMobile: true;
        tablet: true;
        "2xl": true;
        "3xl": true;
        "4xl": true;
        "5xl": true;
    }
}

declare module "@mui/material/Button" {
    interface ButtonPropsVariantOverrides {
        functional: true;
    }
}

/**
 * Custom FPP (SENEC Cockpit) theme for Material UI components
 * includes breakpoints, color palette, and Typography
 *
 * SENEC Design System:
 * https://www.figma.com/file/m7zahjrlKoOtBWzTYcZ6c1/SENEC---Design-System?type=design&node-id=1054-225668&mode=design&t=fmfrovkN7PCdkM64-0
 *
 * For the customizing options refer to the official Material UI documentation:
 * https://mui.com/material-ui/customization/theming/
 */

const baseTheme = createTheme({
    breakpoints,
});

const paletteTheme = createPaletteTheme(baseTheme) as SenecFPPTheme;

const typographyTheme = createTypographyTheme(baseTheme, paletteTheme);

const componentsTheme = createTheme(typographyTheme, {
    components: {
        MuiCssBaseline: {
            styleOverrides: `
            @font-face {
                font-family: 'Mark Pro';
                font-style: normal;
                font-display: swap;
                font-weight: 500;   
                src: url(${MarkProMedium}) format("openType");
            }
            @font-face {
                font-family: 'EnBW DIN Pro';
                font-style: normal;
                font-display: swap;
                font-weight: 400;
                src: url(${EnBWDINPro}) format('trueType');
            }
            @font-face {
                font-family: 'EnBW DIN Pro Medium';
                font-style: normal;
                font-display: swap;
                font-weight: 400;
                src: url(${EnBWDINProMedium}) format('trueType');
            }
            a { color: ${paletteTheme.palette.deepBlue.main}; }
            `,
        },
        MuiChip: senecChipTheme,
        MuiButton: senecButtonTheme,
        MuiIconButton: senecIconButtonTheme,
        MuiTooltip: senecTooltipTheme(paletteTheme),
        MuiInputBase: senecInputBaseTheme(paletteTheme),
        MuiOutlinedInput: senecOutlinedInputTheme(paletteTheme),
        MuiInputLabel: senecInputLabelTheme(paletteTheme),
        MuiNativeSelect: senecNativeSelectTheme,
        MuiSelect: senecSelectTheme(paletteTheme),
        MuiAutocomplete: senecAutocompleteTheme(paletteTheme),
        MuiFormControl: senecFormControlTheme,
        MuiFormHelperText: senecFormHelperTextTheme(typographyTheme),
        MuiMenuItem: senecMenuItemTheme(paletteTheme),
        MuiMenu: senecMenuTheme,
        MuiTabs: senecTabsTheme(paletteTheme),
        MuiTab: senecTabTheme(paletteTheme),
        MuiBadge: senecBadgeTheme(paletteTheme),
        MuiAlert: senecAlertTheme(paletteTheme),
        MuiCheckbox: SenecCheckboxTheme(paletteTheme),
        MuiDataGrid: senecDataGridTheme(paletteTheme),
        MuiPaper: senecPaperTheme,
        MuiStepConnector: senecStepConnectorTheme(paletteTheme),
        MuiStepLabel: senecStepLabelTheme(paletteTheme),
        MuiStepper: senecStepperTheme,
        MuiTablePagination: SenecTablePaginationTheme,
    },
});

export const senecFPPTheme = componentsTheme as SenecFPPTheme;
